/**
 * Datos estructurados (JSON-LD) por tipo de pagina.
 *
 * Se emite un unico `<script type="application/ld+json">` con un `@graph`, que
 * se sobrescribe en cada navegacion. Al ser una SPA el <head> persiste, asi que
 * reutilizar un solo nodo evita que se acumulen los de las paginas ya visitadas.
 *
 * Todo se genera a partir del nombre de ruta y el idioma activo, reutilizando
 * las URLs canonicas de `head.js`, de modo que el marcado sale correcto en los
 * cinco idiomas sin duplicar configuracion.
 */
import messages from '@/translations';

import { CANONICAL_ORIGIN, buildUrl, getCanonicalKey, getOgImageUrl } from './head';
import {
  ORGANIZATION_ID,
  ORGANIZATION_NAME,
  WEBSITE_ID,
  buildHeadquartersAddress,
  buildOrganization,
} from './organization';

/** Id del <script> que contiene el grafo, para poder reutilizarlo. */
const JSONLD_ELEMENT_ID = 'seo-jsonld';

/**
 * Tipo de pagina por ruta. Las ausentes son `WebPage`.
 *
 * El blog queda fuera a proposito: no entra en el alcance del trabajo de SEO.
 */
const PAGE_TYPE_BY_ROUTE = {
  productos: 'CollectionPage',
  productos_quimicos: 'CollectionPage',
  catalogo: 'CollectionPage',
  sala_prensa: 'CollectionPage',
  actualidad: 'CollectionPage',
  sobre_nosotros: 'AboutPage',
  aniversario50: 'AboutPage',
  vehiculo_electrico: 'AboutPage',
  contacto: 'ContactPage',
};

/**
 * Nombre corto de cada pagina para las migas, en la clave de `enlaces.textos`
 * que le corresponde (los nombres de ruta y los de `textos` no coinciden).
 *
 * `productos_quimicos`, `bonos_ajusa` y `newsletter_thank` no tienen entrada en
 * `textos`; el primero se resuelve por `nav_options` y los otros dos se quedan
 * sin migas (ver `buildBreadcrumb`).
 */
const BREADCRUMB_TEXT_KEY_BY_ROUTE = {
  home: 'inicio',
  productos: 'productos',
  catalogo: 'catalogo',
  area_tecnica: 'area_tecnica',
  sobre_nosotros: 'sobre_nosotros',
  aniversario50: 'aniversario50',
  vehiculo_electrico: 'vehiculo_electrico',
  actualidad: 'actualidad',
  contacto: 'contacto',
  sala_prensa: 'sala_prensa',
  trabaja_con_nosotros: 'trabaja_con_nosotros',
  mapa_web: 'mapa_web',
  politica: 'politica_privacidad',
  politica_cookies: 'politica_cookies',
  aviso_legal: 'aviso_legal',
  datos_seguros: 'datos_seguros',
};

/** Ruta padre, para el nivel intermedio de las migas. */
const PARENT_ROUTE = {
  product: 'productos',
  productos_quimicos: 'productos',
};

/**
 * Convierte a texto plano el contenido que en `translations` viene con
 * maquetacion. Hace falta porque en `us` diez de las doce descripciones de
 * producto estan envueltas en `<p>`, y `description` no admite HTML.
 */
export function toPlainText(value) {
  if (!value) return '';

  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Nombre corto de una pagina en el idioma activo, o `null` si no se conoce. */
function getPageName(routeName, lang) {
  const langMessages = messages[lang];
  if (!langMessages) return null;

  const textKey = BREADCRUMB_TEXT_KEY_BY_ROUTE[routeName];
  const textos = langMessages.enlaces ? langMessages.enlaces.textos : null;
  if (textKey && textos && textos[textKey]) return textos[textKey];

  // `productos_quimicos` no esta en `textos` pero si es una categoria.
  const navOptions = langMessages.productos ? langMessages.productos.nav_options : null;
  if (navOptions && navOptions[routeName] && navOptions[routeName].title) {
    return navOptions[routeName].title;
  }

  return null;
}

/** Nombre de una categoria de producto a partir del id de la URL. */
function getProductName(lang, id) {
  const key = getCanonicalKey('product', id, lang);
  if (!key) return null;

  const langMessages = messages[lang];
  const product = langMessages.productos ? langMessages.productos[key] : null;
  if (product && product.titulo) return product.titulo;

  const navOptions = langMessages.productos ? langMessages.productos.nav_options : null;
  return navOptions && navOptions[key] ? navOptions[key].title : null;
}

/**
 * Migas de navegacion. Devuelve `null` cuando alguna pagina de la jerarquia no
 * tiene nombre fiable en el idioma activo: es preferible no marcar migas a
 * publicarlas con huecos.
 */
function buildBreadcrumb(routeName, lang, id) {
  // La home es la raiz: no lleva migas propias.
  if (routeName === 'home') return null;

  const items = [];
  const homeName = getPageName('home', lang);
  const homeUrl = buildUrl(lang, 'home');
  if (!homeName || !homeUrl) return null;
  items.push({ name: homeName, url: homeUrl });

  const parentRoute = PARENT_ROUTE[routeName];
  if (parentRoute) {
    const parentName = getPageName(parentRoute, lang);
    const parentUrl = buildUrl(lang, parentRoute);
    if (!parentName || !parentUrl) return null;
    items.push({ name: parentName, url: parentUrl });
  }

  const currentName = routeName === 'product' ? getProductName(lang, id) : getPageName(routeName, lang);
  const currentUrl = buildUrl(lang, routeName, id);
  if (!currentName || !currentUrl) return null;
  items.push({ name: currentName, url: currentUrl });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map(function (item, index) {
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      };
    }),
  };
}

/** Entidad `WebSite`, referenciada por todas las paginas. */
function buildWebSite(lang) {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: CANONICAL_ORIGIN + '/',
    name: ORGANIZATION_NAME,
    inLanguage: messages[lang] ? messages[lang].isotag : undefined,
    publisher: { '@id': ORGANIZATION_ID },
  };
  // Sin `potentialAction`/`SearchAction`: la web no tiene buscador global con
  // URL de resultados, solo filtros en cliente sobre listados de catalogo.
}

/**
 * Entidad `Product` de una categoria. Sin `offers` ni `aggregateRating`: AJUSA
 * no es una tienda y no publica precios ni valoraciones, asi que declararlos
 * seria falso. El marcado es valido igualmente, aunque no genere resultado
 * enriquecido de precio.
 */
function buildProduct(lang, id, canonicalUrl) {
  const key = getCanonicalKey('product', id, lang);
  if (!key) return null;

  const langMessages = messages[lang];
  const product = langMessages.productos ? langMessages.productos[key] : null;
  if (!product) return null;

  const images = [];
  const slider = product.details && product.details.intro ? product.details.intro.slider : null;
  if (slider) {
    slider.forEach(function (slide) {
      if (slide && slide.img) images.push(CANONICAL_ORIGIN + slide.img);
    });
  }
  // La imagen de Open Graph siempre existe y sirve de respaldo.
  if (!images.length) images.push(getOgImageUrl('product', lang, id));

  return {
    '@type': 'Product',
    '@id': canonicalUrl + '#product',
    name: toPlainText(product.titulo),
    description: toPlainText(product.texto),
    image: images,
    url: canonicalUrl,
    category: getPageName('productos', lang) || undefined,
    brand: { '@type': 'Brand', name: ORGANIZATION_NAME },
    manufacturer: { '@id': ORGANIZATION_ID },
  };
}

/** `ItemList` con las categorias de producto, para el indice de productos. */
function buildProductItemList(lang) {
  const langMessages = messages[lang];
  const navOptions = langMessages.productos ? langMessages.productos.nav_options : null;
  if (!navOptions) return null;

  const items = [];
  Object.keys(navOptions).forEach(function (key) {
    const option = navOptions[key];
    if (!option || typeof option.id !== 'string' || option.id === '') return;

    const url = buildUrl(lang, 'product', option.id);
    if (!url) return;

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: toPlainText(option.title),
      url: url,
    });
  });

  if (!items.length) return null;

  return {
    '@type': 'ItemList',
    numberOfItems: items.length,
    itemListElement: items,
  };
}

/**
 * Construye el grafo JSON-LD completo de una pagina.
 *
 * `seo` son los metadatos de `enlaces.seo` que ya resuelve el router
 * (title/description), para no repetir su logica aqui.
 */
export function buildGraph(routeName, lang, id, seo) {
  const canonicalUrl = buildUrl(lang, routeName, id);
  if (!canonicalUrl || !messages[lang]) return null;

  const graph = [buildOrganization(), buildWebSite(lang)];

  const webPage = {
    '@type': PAGE_TYPE_BY_ROUTE[routeName] || 'WebPage',
    '@id': canonicalUrl + '#webpage',
    url: canonicalUrl,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID },
    inLanguage: messages[lang].isotag,
    primaryImageOfPage: getOgImageUrl(routeName, lang, id),
  };

  const pageName = routeName === 'product' ? getProductName(lang, id) : getPageName(routeName, lang);
  if (pageName) webPage.name = toPlainText(pageName);
  if (seo && seo.title) webPage.headline = toPlainText(seo.title);
  if (seo && seo.description) webPage.description = toPlainText(seo.description);

  const breadcrumb = buildBreadcrumb(routeName, lang, id);
  if (breadcrumb) {
    breadcrumb['@id'] = canonicalUrl + '#breadcrumb';
    webPage.breadcrumb = { '@id': breadcrumb['@id'] };
  }

  // La pagina de contacto describe la sede, asi que lleva la direccion completa.
  if (routeName === 'contacto') {
    webPage.mainEntity = { '@id': ORGANIZATION_ID };
    webPage.address = buildHeadquartersAddress();
  }

  graph.push(webPage);
  if (breadcrumb) graph.push(breadcrumb);

  if (routeName === 'product' && id) {
    const product = buildProduct(lang, id, canonicalUrl);
    if (product) {
      product.isPartOf = { '@id': webPage['@id'] };
      graph.push(product);
    }
  }

  if (routeName === 'productos') {
    const itemList = buildProductItemList(lang);
    if (itemList) {
      itemList['@id'] = canonicalUrl + '#itemlist';
      webPage.mainEntity = { '@id': itemList['@id'] };
      graph.push(itemList);
    }
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

/**
 * Escribe el grafo en el <head>, reutilizando siempre el mismo <script>.
 * Si la ruta no permite construirlo, se elimina el que hubiera para no dejar
 * marcado de la pagina anterior.
 */
export function applyJsonLd(routeName, lang, id, seo) {
  const graph = buildGraph(routeName, lang, id, seo);
  let element = document.getElementById(JSONLD_ELEMENT_ID);

  if (!graph) {
    if (element) element.parentNode.removeChild(element);
    return null;
  }

  if (!element) {
    element = document.createElement('script');
    element.setAttribute('type', 'application/ld+json');
    element.setAttribute('id', JSONLD_ELEMENT_ID);
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(graph);
  return graph;
}



// WEBPACK FOOTER //
// ./src/seo/jsonld.js